<template>
  <section ref="panel" class="profile-onboarding" :aria-label="$t('learnerProfile.onboardingTitle')">
    <header class="onboarding-header">
      <button v-if="stepIndex > 0" type="button" class="icon-button" :aria-label="$t('learnerProfile.back')" @click="goTo(stepIndex - 1)">
        <span class="material-icons" aria-hidden="true">chevron_left</span>
      </button>
      <div class="step-progress" aria-hidden="true">
        <span v-for="(item, index) in steps" :key="item" :class="['step-segment', { done: index <= stepIndex }]"></span>
      </div>
      <button type="button" class="text-button" @click="skip">{{ $t('learnerProfile.setupLater') }}</button>
    </header>

    <Transition :name="direction" mode="out-in">
      <div :key="step" ref="stepContent" tabindex="-1" class="onboarding-content">
        <template v-if="step === 'goals' || step === 'interests'">
          <h2 class="step-title">{{ $t(`learnerProfile.${step}Title`) }}</h2>
          <p class="step-hint">{{ $t(`learnerProfile.${step}Hint`) }}</p>
          <LearnerProfileForm v-model="draft" :step="step" bare />
        </template>

        <template v-else-if="step === 'listening'">
          <QuickListeningTest v-if="!draft.listeningLevel || retakingListening" @done="finishQuickTest" />
          <template v-else>
            <h2 class="step-title">{{ $t('quickListening.resultTitle', { level: draft.listeningLevel }) }}</h2>
            <p class="step-hint">{{ $t('quickListening.resultHint') }}</p>
            <LevelScale skill="listening" :level="draft.listeningLevel" />
            <button type="button" class="inline-link" @click="retakingListening = true">{{ $t('quickListening.retake') }}</button>
          </template>
        </template>

        <template v-else-if="draft.speakingLevel">
          <h2 class="step-title">{{ $t('learnerProfile.speakingResultTitle', { level: draft.speakingLevel }) }}</h2>
          <p class="step-hint">{{ $t('learnerProfile.speakingResultHint') }}</p>
          <LevelScale skill="speaking" :level="draft.speakingLevel" />
        </template>

        <template v-else>
          <h2 class="step-title">{{ $t('learnerProfile.speakingSetupTitle') }}</h2>
          <p class="step-hint">{{ $t('learnerProfile.speakingSetupHint') }}</p>
          <div class="setup-test-entry">
            <button type="button" class="primary-button" @click="startSpeakingTest">
              {{ $t('learnerProfile.speakingTestAction') }}
              <span class="material-icons" aria-hidden="true">arrow_forward</span>
            </button>
            <span class="free-note">{{ $t('levelTest.speakingFirstFree') }}</span>
          </div>
        </template>
      </div>
    </Transition>

    <footer v-if="showFooter" class="onboarding-footer">
      <button type="button" :class="canSkipSpeaking ? 'text-button' : 'primary-button'" :disabled="saving" @click="next">
        {{ footerLabel }}
      </button>
    </footer>
  </section>
</template>

<script>
import { ElMessage } from 'element-plus'
import LearnerProfileForm from './LearnerProfileForm.vue'
import LevelScale from './LevelScale.vue'
import QuickListeningTest from './QuickListeningTest.vue'
import { ONBOARDING_STEPS } from '@/utils/learnerProfile'
import { useLearnerProfile } from '@/composables/useLearnerProfile'
import { useViews } from '@/composables/views'

export default {
  name: 'ProfileOnboarding',
  components: { LearnerProfileForm, LevelScale, QuickListeningTest },
  emits: ['complete'],
  setup() {
    const { profile, saveProfile, dismissOnboarding, onboardingSession, saveOnboardingProgress } = useLearnerProfile()
    const { handleViewChange } = useViews()
    return { profile, saveProfile, dismissOnboarding, onboardingSession, saveOnboardingProgress, navigateToView: handleViewChange }
  },
  data() {
    return {
      steps: ONBOARDING_STEPS,
      stepIndex: this.onboardingSession?.step || 0,
      draft: { ...(this.onboardingSession?.draft || this.profile) },
      direction: 'forward',
      retakingListening: false,
      saving: false
    }
  },
  computed: {
    step() {
      return this.steps[this.stepIndex]
    },
    isLastStep() {
      return this.stepIndex === this.steps.length - 1
    },
    // 快测进行中由它自己的按钮推进
    showFooter() {
      return this.step !== 'listening' || Boolean(this.draft.listeningLevel && !this.retakingListening)
    },
    canSkipSpeaking() {
      return this.step === 'speaking' && !this.draft.speakingLevel
    },
    footerLabel() {
      if (this.canSkipSpeaking) return this.$t('learnerProfile.skipSpeaking')
      return this.$t(this.isLastStep ? 'learnerProfile.finishSetup' : 'learnerProfile.next')
    }
  },
  methods: {
    finishQuickTest(level) {
      this.draft = { ...this.draft, listeningLevel: level, listeningSource: 'quick' }
      this.retakingListening = false
      this.saveOnboardingProgress(this.draft, this.stepIndex)
    },
    startSpeakingTest() {
      this.saveOnboardingProgress(this.draft, this.stepIndex, 'speaking')
      this.navigateToView('LevelTest', { skill: 'speaking', setup: true })
    },
    // 上一步比这一步高时，切换后页面会停在下方，滚回面板顶部
    goTo(index) {
      this.direction = index > this.stepIndex ? 'forward' : 'backward'
      this.stepIndex = index
      this.retakingListening = false
      this.saveOnboardingProgress(this.draft, index)
      this.$nextTick(() => {
        this.$refs.stepContent?.focus({ preventScroll: true })
        const panel = this.$refs.panel
        if (panel.getBoundingClientRect().top < 0) panel.scrollIntoView({ block: 'start' })
      })
    },
    skip() {
      this.dismissOnboarding()
      this.$emit('complete')
    },
    async next() {
      if (this.saving) return
      if (!this.isLastStep) {
        this.goTo(this.stepIndex + 1)
        return
      }
      this.saving = true
      try {
        await this.saveProfile(this.draft)
        this.dismissOnboarding()
        this.$emit('complete')
      } catch (error) {
        ElMessage.error(this.$t('learnerProfile.saveFailed'))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.profile-onboarding {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  width: 100%;
  max-width: 720px;
  box-sizing: border-box;
  margin: 0 auto var(--space-8);
  padding: var(--space-6);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
}

.onboarding-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-height: 36px;
}

.step-progress {
  display: grid;
  flex: 1;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: var(--space-1);
}

.step-segment {
  height: 4px;
  border-radius: var(--radius-pill);
  background: var(--border-default);
  transition: background-color 0.2s ease;
}

.step-segment.done {
  background: var(--signal-active-strong);
}

.onboarding-content:focus {
  outline: none;
}

.step-title {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-xl);
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: -0.015em;
}

.step-hint {
  margin: var(--space-1) 0 var(--space-5);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.setup-test-entry {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.setup-test-entry .primary-button {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.setup-test-entry .material-icons {
  font-size: 18px;
}

.free-note {
  color: var(--signal-active-strong);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.inline-link {
  margin-top: var(--space-4);
  padding: 0;
  border: none;
  background: none;
  color: var(--signal-active-strong);
  font: inherit;
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.inline-link:hover {
  text-decoration: underline;
}

.onboarding-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

.primary-button,
.text-button {
  min-height: 44px;
  padding: 0 var(--space-5);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
}

.primary-button {
  border: 1px solid var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
}

.primary-button:disabled {
  border-color: var(--border-default);
  background: var(--surface-subtle);
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.text-button {
  padding: 0 var(--space-3);
  border: none;
  background: transparent;
  color: var(--ink-secondary);
}

.text-button:hover {
  color: var(--ink-primary);
}

.icon-button {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-left: calc(-1 * var(--space-2));
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--ink-secondary);
  cursor: pointer;
}

.icon-button:hover {
  background: var(--surface-subtle);
  color: var(--ink-primary);
}

.primary-button:focus-visible,
.text-button:focus-visible,
.icon-button:focus-visible,
.inline-link:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

.forward-enter-active,
.forward-leave-active,
.backward-enter-active,
.backward-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.forward-enter-from,
.backward-leave-to {
  opacity: 0;
  transform: translateX(24px);
}

.forward-leave-to,
.backward-enter-from {
  opacity: 0;
  transform: translateX(-24px);
}

@media (prefers-reduced-motion: reduce) {
  .step-segment,
  .forward-enter-active,
  .forward-leave-active,
  .backward-enter-active,
  .backward-leave-active {
    transition: none;
  }
}

@media (max-width: 640px) {
  .profile-onboarding {
    padding: var(--space-4);
  }

  .onboarding-footer button {
    flex: 1;
  }
}
</style>
